<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import * as zod from 'zod'

// Config
defineProps<{
  fields: null
}>()

defineSlice({
  name: {
    label: 'Seadust Weddings Form',
    group: 'Seadust',
    layouts: ['Seadust'],
  },
  templates: [{
    label: 'Seadust Weddings Form',
    fields: [],
  }],
})

const validationSchema = toTypedSchema(
  zod.object({
    first_name: zod.string({ message: '*First Name is required' }).min(1, { message: '*Please provide a valid First Name' }),
    last_name: zod.string({ message: '*Last Name is required' }).min(1, { message: '*Please provide a valid Last Name' }),
    email: zod.string({ message: '*Email is required' }).email({ message: '*Please provide a valid email' }).min(4),
    phone: zod.string({ message: '*Phone is required' }).min(1, { message: '*Please provide a valid phone number' }),
    eventDate: zod.string({ message: '*Event date is required' }).min(1, { message: '*Please provide a valid phone number' }),
    eventPackage: zod.string({ message: '*Package required' }).min(1, { message: '*Please select a wedding package' }),
    comment: zod.string({ message: '*Comment required' }).min(1, { message: '*Please provide a valid comment' }),
    policy_accepted: zod.literal(true),
  }),
)

// state

const formSubmitted = ref(false)

// methods
async function submitForm() {
  const data = {
    first_name: first_name.value,
    last_name: last_name.value,
    email: email.value,
    phone: phone.value,
    date: eventDate.value,
    package: eventPackage.value,
    comment: comment.value,
  }

  await $voixFetch('/api/playa/seadust-forms/weddings', { method: 'post', body: data })

  formSubmitted.value = true
}

const { handleSubmit, errors } = useForm({ validationSchema })
const onSubmit = handleSubmit(() => {
  submitForm()
})

const { value: first_name } = useField('first_name')
const { value: last_name } = useField('last_name')
const { value: email } = useField('email')
const { value: comment } = useField('comment')
const { value: phone } = useField('phone')
const { value: eventDate } = useField('eventDate')
const { value: eventPackage } = useField('eventPackage')
const { value: policy_accepted } = useField('policy_accepted')

first_name.value = ''
last_name.value = ''
email.value = ''
phone.value = ''
eventDate.value = ''
eventPackage.value = ''
policy_accepted.value = false
</script>

<template>
  <div class="flex flex-col justify-center items-center w-full p-8 lg:p-16">
    <!-- Heading & Copy -->
    <div class="flex flex-col w-full lg:w-[800px] xl:w-[1200px]">
      <div class="heading w-full">
        <div class="title">
          Information Request - Weddings
        </div>
      </div>

      <div class="my-2">
        Information request
      </div>

      <div class="my-2">
        The fields marked with '*' are mandatory
      </div>
    </div>

    <!-- Form -->
    <form
      v-if="!formSubmitted"
      class="mt-8 w-full lg:w-[800px] xl:w-[1200px]"
      @submit.prevent="onSubmit"
    >
      <fieldset
        class="grid grid-cols-1 lg:grid-cols-2 gap-4"
      >
        <!-- First Name -->
        <div class="flex flex-col w-full">
          <label for="id_nombres">First Name</label>
          <input
            id="id_nombres"
            v-model="first_name"
            type="text" name="nombres"
            maxlength="200"
            minlength="0" required
            placeholder=""
            class=""
          >
          <div v-if="errors.first_name" class="text-left py-2 px-2 rounded-b text-grey italic text-xs">
            {{ errors.first_name }}
          </div>
        </div>

        <!-- Last Name -->
        <div class="flex flex-col w-full">
          <label for="id_apellidos">Last Name</label>
          <input
            v-model="last_name" type="text"
            name="apellidos" maxlength="200"
            minlength="0" required
            placeholder=""
            class=""
          >
          <div v-if="errors.last_name" class="text-left py-2 px-2 rounded-b text-grey italic text-xs">
            {{ errors.last_name }}
          </div>
        </div>

        <!-- Email -->
        <div class="flex flex-col w-full">
          <label for="id_correo-electronico">Email</label>
          <input
            v-model="email"
            type="email"
            name="correo-electronico"
            required placeholder=""
            class=""
          >
          <div v-if="errors.email" class="text-left py-2 px-2 rounded-b text-grey italic text-xs">
            {{ errors.email }}
          </div>
        </div>

        <!-- Phone -->
        <div class="flex flex-col w-full">
          <label for="id_telefono">Phone</label>
          <input
            v-model="phone" type="text"
            name="telefono"
            placeholder=""
            class=""
          >
          <div v-if="errors.phone" class="text-left py-2 px-2 rounded-b text-grey italic text-xs">
            {{ errors.phone }}
          </div>
        </div>

        <!-- Event Date -->
        <div class="flex flex-col w-full">
          <label for="id_fecha">Event date (DD/MM/YYYY)</label>
          <input
            v-model="eventDate" type="text"
            name="fecha"
            data-is-one-day-only="true" placeholder=""
            class=""
          >
          <div v-if="errors.eventDate" class="text-left py-2 px-2 rounded-b text-grey italic text-xs">
            {{ errors.eventDate }}
          </div>
        </div>

        <!-- Wedding Package -->
        <div class="flex flex-col w-full">
          <label for="id_paquete-de-boda">Wedding package</label>
          <select
            v-model="eventPackage" name="paquete-de-boda"
            placeholder="Select Package"
            class="form__select"
          >
            <option value="My Seadust Wedding">
              My Seadust Wedding
            </option>
            <option value="My Wedding á la Carte">
              My Wedding á la Carte
            </option>
          </select>
          <div v-if="errors.eventPackage" class="text-left py-2 px-2 rounded-b text-grey italic text-xs">
            {{ errors.eventPackage }}
          </div>
        </div>

        <!-- Comments -->
        <div class="grid lg:col-span-2 flex flex-col w-full">
          <label for="id_comentarios">Comments</label>
          <textarea
            v-model="comment" name="comentarios"
            rows="10"
            required placeholder="" cols="40"
            class="form__text-area"
          />
          <div v-if="errors.comment" class="text-left py-2 px-2 rounded-b text-grey italic text-xs">
            {{ errors.comment }}
          </div>
        </div>
      </fieldset>

      <!-- Privacy Policy -->
      <div class="copy my-4">
        <input
          v-model="policy_accepted" type="checkbox" class="mr-2"
        >
        I have read and accept the
        <a href="/en/legal-notice/" target="_blank">
          Privacy Policy
        </a>
        <div v-if="errors.policy_accepted" class="text-left py-2 px-2 rounded-b text-grey italic text-xs">
          *You must accept the Privacy Policy
        </div>
      </div>

      <!-- Submit -->
      <div class="flex justify-center">
        <button type="submit" class="btn">
          Submit
        </button>
      </div>
    </form>
    <div v-else class="mt-8 w-full lg:w-[800px] xl:w-[1200px] bg-white px-12 pt-12 pb-6">
      <div class="heading mx-auto">
        <div class="title">
          Weddings Form Submitted
        </div>
      </div>
      <div class="voix-wysiwyg copy mx-auto w-full">
        <div><p>Thank you for getting in touch! We appreciate you contacting us. One of our colleagues will get back in touch with you soon! Have a great day!</p></div>
      </div>
    </div>
  </div>
</template>
